import { useEffect, useLayoutEffect, useRef } from "react";

function centerTab(container: HTMLElement, activeTab: string, behavior: ScrollBehavior) {
  const selector = `[data-tab-id="${CSS.escape(activeTab)}"]`;
  const btn = container.querySelector<HTMLElement>(selector);
  if (!btn) return;
  if (container.scrollWidth <= container.clientWidth) return;

  const containerRect = container.getBoundingClientRect();
  const btnRect = btn.getBoundingClientRect();
  const target =
    container.scrollLeft +
    (btnRect.left - containerRect.left) -
    containerRect.width / 2 +
    btnRect.width / 2;
  const max = container.scrollWidth - container.clientWidth;
  const clamped = Math.max(0, Math.min(target, max));

  container.scrollTo({ left: clamped, behavior });
}

export function useCenterActiveTab<T extends string | number>(activeTab: T) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    centerTab(container, String(activeTab), "auto");
    const raf = requestAnimationFrame(() => {
      if (containerRef.current) {
        centerTab(containerRef.current, String(activeTab), "auto");
      }
    });
    mountedRef.current = true;
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    centerTab(container, String(activeTab), "smooth");
  }, [activeTab]);

  return containerRef;
}
